import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Receipt, Search, Filter, Download, RefreshCw, Calendar,
  CreditCard, Banknote, TrendingUp, FileText, Eye, ChevronDown,
  Car, MapPin, Clock, User, ChevronLeft, ChevronRight, X
} from 'lucide-react';
import api, { downloadInvoicePdf } from '../../services/api';
import { format, parseISO, startOfDay, endOfDay, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { sq } from 'date-fns/locale/sq';
import { toast } from 'sonner';
import { useTableSort, sortRows } from '../../hooks/useTableSort';
import { SortableTableHead } from '../ui/SortableTableHead';

const INVOICE_TABLE_SORT = {
  number: (inv) => inv.invoice_number || '',
  driver: (inv) => (inv.driver_name || '').toLowerCase(),
  passenger: (inv) => (inv.passenger_name || '').toLowerCase(),
  route: (inv) => `${inv.pickup_address || ''} ${inv.dropoff_address || ''}`.toLowerCase(),
  payment: (inv) => (inv.payment_method || '').toLowerCase(),
  amount: (inv) => Number(inv.total_amount || inv.amount || 0),
  date: (inv) => new Date(inv.created_at || inv.issued_at || 0).getTime(),
};

const InvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [totals, setTotals] = useState({ total_amount: 0, total_cash: 0, total_card: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Date filter state
  const [dateFilter, setDateFilter] = useState('all'); // today, yesterday, week, month, custom, all
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const { sortKey, sortDir, onSort } = useTableSort('date', 'desc');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [syncedOnce, setSyncedOnce] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const itemsPerPage = 100;

  // Get date range based on filter
  const getDateRange = useCallback(() => {
    const now = new Date();
    switch (dateFilter) {
      case 'today':
        return {
          from: startOfDay(now).toISOString(),
          to: endOfDay(now).toISOString()
        };
      case 'yesterday':
        const yesterday = subDays(now, 1);
        return {
          from: startOfDay(yesterday).toISOString(),
          to: endOfDay(yesterday).toISOString()
        };
      case 'week':
        return {
          from: startOfWeek(now, { weekStartsOn: 1 }).toISOString(),
          to: endOfWeek(now, { weekStartsOn: 1 }).toISOString()
        };
      case 'month':
        return {
          from: startOfMonth(now).toISOString(),
          to: endOfMonth(now).toISOString()
        };
      case 'custom':
        return {
          from: customDateFrom ? startOfDay(new Date(customDateFrom)).toISOString() : null,
          to: customDateTo ? endOfDay(new Date(customDateTo)).toISOString() : null
        };
      case 'all':
        return { from: null, to: null };
      default:
        return {
          from: startOfDay(now).toISOString(),
          to: endOfDay(now).toISOString()
        };
    }
  }, [dateFilter, customDateFrom, customDateTo]);

  const fetchInvoices = useCallback(async (options = {}) => {
    const { forceSync = false } = options;
    try {
      setLoading(true);
      const params = {
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage
      };
      
      if (paymentFilter !== 'all') {
        params.payment_method = paymentFilter;
      }
      
      const { from, to } = getDateRange();
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      if (forceSync || !syncedOnce) params.sync_missing = true;

      const response = await api.get('/invoices', { params });
      setInvoices(response.data.invoices || []);
      setTotals(response.data.totals || { total_amount: 0, total_cash: 0, total_card: 0, count: 0 });
      setTotalCount(response.data.totals?.count || 0);
      if (!syncedOnce) setSyncedOnce(true);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error(error.response?.data?.detail || 'Gabim në ngarkimin e faturave');
    } finally {
      setLoading(false);
    }
  }, [paymentFilter, currentPage, getDateRange, syncedOnce]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [paymentFilter, dateFilter, customDateFrom, customDateTo]);

  const filteredInvoices = useMemo(() => {
    if (!searchTerm) return invoices;
    const term = searchTerm.toLowerCase();
    return invoices.filter(inv => 
      inv.invoice_number?.toLowerCase().includes(term) ||
      inv.driver_name?.toLowerCase().includes(term) ||
      inv.passenger_name?.toLowerCase().includes(term) ||
      inv.pickup_address?.toLowerCase().includes(term) ||
      inv.dropoff_address?.toLowerCase().includes(term)
    );
  }, [invoices, searchTerm]);

  const displayedInvoices = useMemo(
    () => sortRows(filteredInvoices, sortKey, sortDir, INVOICE_TABLE_SORT),
    [filteredInvoices, sortKey, sortDir],
  );

  const formatPrice = (amount) => {
    return Number(amount || 0).toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    try {
      return format(parseISO(dateStr), 'dd MMM yyyy, HH:mm', { locale: sq });
    } catch {
      return dateStr;
    }
  };

  const getPaymentBadge = (method) => {
    const badges = {
      cash: { bg: 'bg-green-100', text: 'text-green-700', icon: Banknote, label: 'Cash' },
      card: { bg: 'bg-blue-100', text: 'text-blue-700', icon: CreditCard, label: 'Kartë' },
      transfer: { bg: 'bg-purple-100', text: 'text-purple-700', icon: Receipt, label: 'Transfer' }
    };
    const badge = badges[method] || badges.cash;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const openInvoiceDetail = (invoice) => {
    setSelectedInvoice(invoice);
    setShowDetailModal(true);
  };

  const handleDownloadPdf = async (invoice) => {
    if (!invoice?.id) return;
    try {
      setDownloadingPdf(true);
      await downloadInvoicePdf(invoice.id, invoice.invoice_number || 'fature');
      toast.success('PDF u shkarkua');
    } catch (error) {
      console.error('Error downloading invoice PDF:', error);
      toast.error(error.response?.data?.detail || 'Shkarkimi i PDF dështoi');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const getDateFilterLabel = () => {
    switch (dateFilter) {
      case 'today': return 'Sot';
      case 'yesterday': return 'Dje';
      case 'week': return 'Kjo Javë';
      case 'month': return 'Ky Muaj';
      case 'custom': return 'Periudhë';
      case 'all': return 'Të Gjitha';
      default: return 'Të Gjitha';
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Faturat</h1>
          <p className="text-sm text-gray-500">Menaxho dhe shiko të gjitha faturat e sistemit</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={async () => {
              try {
                setLoading(true);
                await api.post('/finance/sync-invoices');
                setSyncedOnce(true);
                await fetchInvoices({ forceSync: false });
                toast.success('Faturat u sinkronizuan nga udhëtimet');
              } catch (error) {
                toast.error(error.response?.data?.detail || 'Sinkronizimi dështoi');
              } finally {
                setLoading(false);
              }
            }}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition"
          >
            <Download className="w-4 h-4" />
            Sinkronizo
          </button>
          <button
            type="button"
            onClick={() => fetchInvoices({ forceSync: true })}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Rifresko
          </button>
        </div>
      </div>

      {dateFilter !== 'all' && !loading && invoices.length === 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Nuk ka fatura për periudhën &quot;{getDateFilterLabel()}&quot;. Provo{' '}
          <button type="button" className="font-semibold underline" onClick={() => setDateFilter('all')}>
            Të Gjitha Kohët
          </button>
          {' '}ose ndrysho filtrin e datës.
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Të Ardhura ({getDateFilterLabel()})</p>
              <p className="text-3xl font-bold mt-1">{formatPrice(totals.total_amount)} L</p>
              <p className="text-green-200 text-xs mt-1">{totals.count || 0} fatura</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <TrendingUp className="w-6 h-6" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Të Ardhura (LEK)</p>
              <p className="text-3xl font-bold mt-1">{formatPrice(totals.total_amount)} L</p>
              <p className="text-blue-200 text-xs mt-1">≈ konvertim</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <Receipt className="w-6 h-6" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-100 text-sm">Cash ({getDateFilterLabel()})</p>
              <p className="text-3xl font-bold mt-1">{formatPrice(totals.total_cash)} L</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <Banknote className="w-6 h-6" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Kartë ({getDateFilterLabel()})</p>
              <p className="text-3xl font-bold mt-1">{formatPrice(totals.total_card)} L</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <CreditCard className="w-6 h-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center gap-4">
        {/* Search */}
        <div className="flex-1 min-w-[250px] relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Kërko sipas numrit të faturës, shoferit, pasagjerit..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
          />
        </div>

        {/* Date Filter */}
        <div className="relative">
          <button
            onClick={() => setShowDatePicker(!showDatePicker)}
            className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
          >
            <Calendar className="w-4 h-4 text-gray-500" />
            <span>{getDateFilterLabel()}</span>
            <ChevronDown className="w-4 h-4 text-gray-400" />
          </button>
          
          {showDatePicker && (
            <div className="absolute top-full mt-2 left-0 bg-white border border-gray-200 rounded-xl shadow-xl z-50 w-72 p-4">
              <div className="space-y-2">
                {[
                  { key: 'today', label: 'Sot' },
                  { key: 'yesterday', label: 'Dje' },
                  { key: 'week', label: 'Kjo Javë' },
                  { key: 'month', label: 'Ky Muaj' },
                  { key: 'all', label: 'Të Gjitha Kohët' },
                ].map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => { setDateFilter(opt.key); setShowDatePicker(false); }}
                    className={`w-full text-left px-3 py-2 rounded-lg transition ${
                      dateFilter === opt.key ? 'bg-red-50 text-red-600 font-medium' : 'hover:bg-gray-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
                
                <div className="border-t border-gray-200 pt-3 mt-3">
                  <p className="text-xs text-gray-500 mb-2 font-medium">Periudhë e Personalizuar</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={customDateFrom}
                      onChange={(e) => setCustomDateFrom(e.target.value)}
                      className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                    <span className="text-gray-400">-</span>
                    <input
                      type="date"
                      value={customDateTo}
                      onChange={(e) => setCustomDateTo(e.target.value)}
                      className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                  </div>
                  <button
                    onClick={() => { setDateFilter('custom'); setShowDatePicker(false); }}
                    disabled={!customDateFrom || !customDateTo}
                    className="w-full mt-2 px-3 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Apliko
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Payment Filter */}
        <select
          value={paymentFilter}
          onChange={(e) => setPaymentFilter(e.target.value)}
          className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
        >
          <option value="all">Të gjitha Pagesat</option>
          <option value="cash">Vetëm Cash</option>
          <option value="card">Vetëm Kartë</option>
        </select>

        <button className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition">
          <Download className="w-4 h-4" />
          Eksporto
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <SortableTableHead label="Nr. Faturës" field="number" activeField={sortKey} sortDir={sortDir} onSort={onSort} className="font-medium" />
              <SortableTableHead label="Shoferi" field="driver" activeField={sortKey} sortDir={sortDir} onSort={onSort} className="font-medium" />
              <SortableTableHead label="Pasagjeri" field="passenger" activeField={sortKey} sortDir={sortDir} onSort={onSort} className="font-medium" />
              <SortableTableHead label="Rruga" field="route" activeField={sortKey} sortDir={sortDir} onSort={onSort} className="font-medium" />
              <SortableTableHead label="Pagesa" field="payment" activeField={sortKey} sortDir={sortDir} onSort={onSort} align="center" className="font-medium" />
              <SortableTableHead label="Shuma" field="amount" activeField={sortKey} sortDir={sortDir} onSort={onSort} align="right" className="font-medium" />
              <SortableTableHead label="Data" field="date" activeField={sortKey} sortDir={sortDir} onSort={onSort} align="right" className="font-medium" />
              <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">Veprime</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="8" className="px-4 py-12 text-center">
                  <RefreshCw className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500">Duke ngarkuar faturat...</p>
                </td>
              </tr>
            ) : displayedInvoices.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-4 py-12 text-center">
                  <Receipt className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">Nuk ka fatura për këtë periudhë</p>
                </td>
              </tr>
            ) : (
              displayedInvoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-gray-50 transition">
                  <td className="px-4 py-3">
                    <span className="font-mono text-sm font-medium text-gray-900">
                      {invoice.invoice_number}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center text-white text-xs font-bold">
                        {invoice.driver_name?.charAt(0) || 'D'}
                      </div>
                      <span className="text-gray-900">{invoice.driver_name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-gray-600">{invoice.passenger_name || 'Pasagjer'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="max-w-[240px] space-y-1">
                      {/* Pickup - inline with green dot */}
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full bg-green-500 flex-shrink-0" />
                        <span className="text-xs text-gray-700 truncate" title={invoice.pickup_address}>
                          {invoice.pickup_address?.substring(0, 28)}{invoice.pickup_address?.length > 28 ? '...' : ''}
                        </span>
                      </div>
                      {/* Dropoff - inline with red dot */}
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full bg-red-500 flex-shrink-0" />
                        <span className="text-xs text-gray-500 truncate" title={invoice.dropoff_address}>
                          {invoice.dropoff_address?.substring(0, 28)}{invoice.dropoff_address?.length > 28 ? '...' : ''}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {getPaymentBadge(invoice.payment_method)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="font-semibold text-gray-900">{formatPrice(invoice.total_amount)} L</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-sm text-gray-500">{formatDate(invoice.created_at)}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => openInvoiceDetail(invoice)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition"
                      title="Shiko Detajet"
                    >
                      <Eye className="w-4 h-4 text-gray-600" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white rounded-xl border border-gray-200 px-4 py-3">
          <div className="text-sm text-gray-500">
            Shfaq {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalCount)} nga {totalCount} fatura
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            
            {/* Page numbers */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`w-8 h-8 rounded-lg text-sm font-medium transition ${
                    currentPage === pageNum 
                      ? 'bg-red-600 text-white' 
                      : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Totals Bar */}
      <div className="bg-gray-900 rounded-xl p-4 flex items-center justify-between text-white">
        <div className="flex items-center gap-8">
          <div>
            <p className="text-gray-400 text-xs">Fatura të shfaqura</p>
            <p className="text-xl font-bold">{displayedInvoices.length}</p>
          </div>
          <div className="w-px h-10 bg-gray-700" />
          <div>
            <p className="text-gray-400 text-xs">Total Cash</p>
            <p className="text-xl font-bold text-green-400">{formatPrice(totals.total_cash)} L</p>
          </div>
          <div>
            <p className="text-gray-400 text-xs">Total Kartë</p>
            <p className="text-xl font-bold text-blue-400">{formatPrice(totals.total_card)} L</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-gray-400 text-xs">TOTALI ({getDateFilterLabel()})</p>
          <p className="text-3xl font-bold text-yellow-400">{formatPrice(totals.total_amount)} L</p>
        </div>
      </div>

      {/* Invoice Detail Modal */}
      {showDetailModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-600 to-red-700 p-6 rounded-t-2xl text-white relative">
              <button
                onClick={() => setShowDetailModal(false)}
                className="absolute top-4 right-4 p-1 hover:bg-white/20 rounded-full transition"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-red-200 text-sm">Faturë</p>
                  <p className="text-2xl font-bold">{selectedInvoice.invoice_number}</p>
                </div>
                <div className="text-right">
                  <p className="text-red-200 text-sm">Totali</p>
                  <p className="text-3xl font-bold">{formatPrice(selectedInvoice.total_amount)} L</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* Driver & Passenger */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <Car className="w-3 h-3" /> Shoferi
                  </p>
                  <p className="font-semibold text-gray-900">{selectedInvoice.driver_name}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <User className="w-3 h-3" /> Pasagjeri
                  </p>
                  <p className="font-semibold text-gray-900">{selectedInvoice.passenger_name}</p>
                </div>
              </div>

              {/* Route - Inline style */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500 flex items-center gap-1 mb-3">
                  <MapPin className="w-3 h-3" /> Rruga
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                    <span className="text-sm text-gray-700">{selectedInvoice.pickup_address}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <span className="text-sm text-gray-700">{selectedInvoice.dropoff_address}</span>
                  </div>
                </div>
              </div>

              {/* Details */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Distanca</p>
                  <p className="font-bold text-gray-900">{selectedInvoice.distance_km?.toFixed(1) || '--'} km</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Kohëzgjatja</p>
                  <p className="font-bold text-gray-900">{Math.round(selectedInvoice.duration_minutes || 0)} min</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Pagesa</p>
                  {getPaymentBadge(selectedInvoice.payment_method)}
                </div>
              </div>

              {/* Price Breakdown */}
              <div className="border-t border-gray-200 pt-4 space-y-2">
                <div className="flex justify-between text-lg font-bold">
                  <span>TOTALI</span>
                  <span className="text-red-600">{formatPrice(selectedInvoice.total_amount)} L</span>
                </div>
                <div className="text-right text-sm text-gray-400">
                  ≈ {selectedInvoice.total_amount?.toLocaleString() || 0} L
                </div>
              </div>

              {/* Date */}
              <div className="text-center text-xs text-gray-400 pt-2">
                <Clock className="w-3 h-3 inline mr-1" />
                {formatDate(selectedInvoice.created_at)}
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 p-4 flex gap-3">
              <button
                onClick={() => setShowDetailModal(false)}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition"
              >
                Mbyll
              </button>
              <button
                type="button"
                onClick={() => handleDownloadPdf(selectedInvoice)}
                disabled={downloadingPdf}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Download className={`w-4 h-4 ${downloadingPdf ? 'animate-pulse' : ''}`} />
                {downloadingPdf ? 'Duke shkarkuar...' : 'Shkarko PDF'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicesPage;
